/** Sign-in, invite (set your password) and terms of use. */
import { useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError, setToken, type Me } from "../api.ts";
import { Logo, Problem, Spinner, useLoad, Wordmark } from "../ui.tsx";

function AuthFrame({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="auth">
      <div className={`auth-wrap ${wide ? "wide" : ""}`}>
        <div className="auth-brand">
          <Logo size={36} />
          <div>
            <Wordmark />
            <div className="brand-sub">Remote water and pump control</div>
          </div>
        </div>
        <div className={`card auth-card ${wide ? "wide" : ""}`}>{children}</div>
      </div>
    </div>
  );
}

const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");

export function SignIn({ onSignedIn, hint }: { onSignedIn: () => void; hint?: ReactNode }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { token } = await api.login(email.trim(), password);
      setToken(token);
      onSignedIn();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      <h1 style={{ fontSize: 26 }}>Sign in</h1>
      {hint}
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <button className="btn primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p className="muted small" style={{ margin: 0 }}>
        Forgotten your password or need access? Ask the account owner, or call FMS.
      </p>
      <InstallTip />
    </AuthFrame>
  );
}

/**
 * iPhone/iPad in Safari, not yet opened from the home screen: say how to add it.
 * (iOS has no install prompt of its own.) The home-screen app keeps its own sign-in,
 * separate from Safari's, so adding it before signing in saves doing it twice.
 */
function InstallTip() {
  const nav = navigator as Navigator & { standalone?: boolean };
  const ios = /iPhone|iPad|iPod/.test(nav.userAgent) || (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  const installed = nav.standalone === true || matchMedia("(display-mode: standalone)").matches;
  if (!ios || installed) return null;
  return (
    <p className="muted small" style={{ margin: 0 }}>
      <b>On your phone?</b> Tap the Share button, then <b>Add to Home Screen</b>, and open Think-Control from its icon.
    </p>
  );
}

/** From the emailed link: /invite#<token>. The token stays after "#" so it never reaches server logs. */
export function Invite({ inviteToken, onDone }: { inviteToken: string; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== again) return setError("The two passwords don't match.");
    setBusy(true);
    setError("");
    try {
      const { token } = await api.acceptInvite(inviteToken, password);
      setToken(token);
      onDone();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      <h1 style={{ fontSize: 26 }}>Welcome to Think-Control</h1>
      <p className="muted" style={{ margin: 0 }}>
        Choose a password to finish setting up your sign-in. Use at least 10 characters; a few words strung together works well.
      </p>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="field">
          <label htmlFor="pw1">New password</label>
          <input id="pw1" type="password" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="pw2">Type it again</label>
          <input id="pw2" type="password" autoComplete="new-password" required value={again} onChange={(e) => setAgain(e.target.value)} />
        </div>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <button className="btn primary" disabled={busy}>
          {busy ? "Saving…" : "Set password and continue"}
        </button>
      </form>
    </AuthFrame>
  );
}

/** Every customer user accepts the current terms before using the app, and again when they change. */
export function TermsGate({ me, onAccepted, onSignOut }: { me: Me; onAccepted: () => void; onSignOut: () => void }) {
  const terms = useLoad(() => api.currentTerms(), []);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const accept = async () => {
    if (!terms.data) return;
    setBusy(true);
    setError("");
    try {
      await api.acceptTerms(terms.data.id);
      onAccepted();
    } catch (err) {
      if (err instanceof ApiError && err.code === "terms-changed") terms.reload();
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame wide>
      <div>
        <div className="sub-h">Before you start</div>
        <h1 style={{ fontSize: 26 }}>{terms.data?.title ?? me.terms?.title ?? "Terms of use"}</h1>
        {terms.data && <div className="muted small">Version {terms.data.version}</div>}
      </div>
      {terms.error ? <Problem error={terms.error} onRetry={terms.reload} /> : null}
      {!terms.data && !terms.error && <Spinner />}
      {terms.data && (
        <>
          <div className="terms-body" tabIndex={0}>
            {terms.data.body}
          </div>
          <label className="checkline">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span>I've read these terms and agree to them for my use of Think-Control.</span>
          </label>
          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn primary" disabled={!agree || busy} onClick={accept}>
              {busy ? "Saving…" : "Accept and continue"}
            </button>
            <button className="btn" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        </>
      )}
    </AuthFrame>
  );
}
